import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { 
  DollarSign, Save, Calendar, CreditCard, Banknote,
  ArrowDownCircle, ArrowUpCircle, Wallet, RefreshCw,
  Sunrise, Lock, Unlock, User as UserIcon, Clock, Download
} from 'lucide-react';
import { toast } from 'sonner';

const DailyCashPage = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [cashData, setCashData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [encargados, setEncargados] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [formData, setFormData] = useState({
    encargado: '',
    caja_inicio_efectivo: 0,
    deposito_bancario: 0,
    gastos_agua: 0,
    gastos_reembolso: 0,
    otros_gastos: 0
  });

  const estado = cashData?.estado || 'NO_INICIADA';
  const cerrada = estado === 'CERRADA';
  const abierta = estado === 'ABIERTA';

  const [sugerencia, setSugerencia] = useState(null);

  useEffect(() => {
    axios.get(`${API}/daily-cash/${date}/sugerencia-inicial`, { withCredentials: true })
      .then(r => setSugerencia(r.data))
      .catch(() => setSugerencia(null));
  }, [date]);

  const fetchEncargados = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/daily-cash/encargados`, { withCredentials: true });
      setEncargados(res.data || []);
    } catch (error) {
      console.error('Error fetching encargados:', error);
      toast.error('No se pudo cargar la lista de encargados');
    }
  }, []);

  const fetchCashData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/daily-cash/${date}`, {
        withCredentials: true
      });
      setCashData(response.data);
      setFormData({
        encargado: response.data.encargado || '',
        caja_inicio_efectivo: response.data.caja_inicio_efectivo || 0,
        deposito_bancario: response.data.deposito_bancario || 0,
        gastos_agua: response.data.gastos_agua || 0,
        gastos_reembolso: response.data.gastos_reembolso || 0,
        otros_gastos: response.data.otros_gastos || 0
      });
    } catch (error) {
      console.error('Error fetching cash data:', error);
      setCashData({
        fecha: date,
        debito: 0,
        credito: 0,
        efectivo: 0,
        transferencia: 0,
        otro: 0
      });
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    fetchCashData();
  }, [fetchCashData]);

  // Live refresh of income every 30s (does NOT touch the form being edited)
  const fetchIngresos = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/daily-cash/${date}`, { withCredentials: true });
      setCashData(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error refreshing income:', error);
    }
  }, [date]);

  useEffect(() => {
    const interval = setInterval(fetchIngresos, 30000);
    const onFocus = () => fetchIngresos();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchIngresos]);

  const downloadCierrePdf = async () => {
    try {
      const r = await axios.get(`${API}/daily-cash/${date}/cierre-pdf`, {
        withCredentials: true, responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `cierre_caja_${date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      toast.success('PDF de cierre descargado');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al descargar el PDF de cierre');
    }
  };

  useEffect(() => {
    fetchEncargados();
  }, [fetchEncargados]);

  const handleAbrir = async () => {
    if (!formData.encargado) {
      toast.error('Selecciona el encargado del día');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/daily-cash/${date}/abrir`, {
        encargado: formData.encargado,
        caja_inicio_efectivo: formData.caja_inicio_efectivo || 0
      }, { withCredentials: true });
      toast.success(`Día iniciado — Encargado: ${formData.encargado}`);
      fetchCashData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar el día');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/daily-cash`, 
        { fecha: date, ...formData },
        { withCredentials: true }
      );
      toast.success('Datos guardados');
      fetchCashData();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleCerrar = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/daily-cash/${date}/cerrar`,
        { fecha: date, ...formData },
        { withCredentials: true }
      );
      toast.success('Caja cerrada correctamente');
      fetchCashData();
      downloadCierrePdf();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar la caja');
    } finally {
      setSaving(false);
    }
  };

  const handleReabrir = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/daily-cash/${date}/reabrir`, {}, { withCredentials: true });
      toast.success('Caja reabierta para edición');
      fetchCashData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reabrir la caja');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatHora = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  // Options for encargado select (include saved value even if not in list)
  const encargadoOptions = formData.encargado && !encargados.includes(formData.encargado)
    ? [formData.encargado, ...encargados]
    : encargados;

  // Calculate totals
  const totalTransbank = (cashData?.debito || 0) + (cashData?.credito || 0);
  const totalIngresos = totalTransbank + (cashData?.efectivo || 0) + 
    (cashData?.transferencia || 0) + (cashData?.otro || 0);
  const totalGastos = (formData.gastos_agua || 0) + (formData.gastos_reembolso || 0) + 
    (formData.otros_gastos || 0);
  const totalCajaEfectivo = (formData.caja_inicio_efectivo || 0) + 
    (cashData?.efectivo || 0) - totalGastos - (formData.deposito_bancario || 0);

  const EncargadoSelect = ({ disabled }) => (
    <Select
      value={formData.encargado || ''}
      onValueChange={(v) => setFormData({ ...formData, encargado: v })}
      disabled={disabled}
    >
      <SelectTrigger className="input-clinical mt-1" data-testid="select-encargado">
        <SelectValue placeholder="Seleccionar encargado..." />
      </SelectTrigger>
      <SelectContent>
        {encargadoOptions.map(n => (
          <SelectItem key={n} value={n}>{n}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="daily-cash-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 heading">Caja Diaria</h1>
            <p className="text-slate-500 mt-1">Control de ingresos y gastos del día</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Estado badge */}
            {!loading && (
              abierta ? (
                <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 gap-1 py-1" data-testid="estado-caja-badge">
                  <Unlock className="w-3 h-3" />
                  Caja abierta {cashData?.apertura_at ? `a las ${formatHora(cashData.apertura_at)}` : ''} — {cashData?.encargado}
                </Badge>
              ) : cerrada ? (
                <Badge className="bg-slate-200 text-slate-700 border border-slate-300 gap-1 py-1" data-testid="estado-caja-badge">
                  <Lock className="w-3 h-3" />
                  Caja cerrada {cashData?.cierre_at ? `a las ${formatHora(cashData.cierre_at)}` : ''}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-amber-600 border-amber-300 gap-1 py-1" data-testid="estado-caja-badge">
                  <Sunrise className="w-3 h-3" />
                  Día sin iniciar
                </Badge>
              )
            )}
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-400" />
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-40 input-clinical"
                data-testid="date-picker"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={fetchCashData}
              className="gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="loading-spinner w-10 h-10"></div>
          </div>
        ) : estado === 'NO_INICIADA' ? (
          /* ===== INICIO DEL DÍA ===== */
          <div className="max-w-lg mx-auto">
            <Card className="border-amber-200" data-testid="inicio-dia-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <Sunrise className="w-6 h-6" />
                  Inicio del Día
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Registra quién queda a cargo y con cuánto efectivo inicia la caja. 
                  Quedará guardado para el cierre de la tarde.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="flex items-center gap-1">
                    <UserIcon className="w-4 h-4" />
                    Encargado del día
                  </Label>
                  <EncargadoSelect />
                </div>
                <div>
                  <Label htmlFor="caja_inicio_apertura">Caja Inicio Efectivo ($)</Label>
                  <Input
                    id="caja_inicio_apertura"
                    type="number"
                    value={formData.caja_inicio_efectivo}
                    onChange={(e) => setFormData({...formData, caja_inicio_efectivo: parseFloat(e.target.value) || 0})}
                    className="input-clinical mt-1"
                    data-testid="input-caja-inicio"
                  />
                  {sugerencia?.fecha_origen && (
                    <div className="mt-2 flex items-center justify-between gap-2 text-xs bg-blue-50 border border-blue-200 rounded-lg px-3 py-2" data-testid="sugerencia-inicial">
                      <span className="text-blue-700">
                        Sugerencia: <strong>{formatCurrency(sugerencia.sugerencia)}</strong> — efectivo que quedó el {sugerencia.fecha_origen.split('-').reverse().join('/')}
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-blue-700 border-blue-300 hover:bg-blue-100 shrink-0"
                        onClick={() => setFormData({...formData, caja_inicio_efectivo: sugerencia.sugerencia})}
                        data-testid="usar-sugerencia-btn"
                      >
                        Usar
                      </Button>
                    </div>
                  )}
                </div>
                <Button
                  onClick={handleAbrir}
                  disabled={saving}
                  className="w-full gap-2 bg-amber-600 hover:bg-amber-700"
                  data-testid="btn-iniciar-dia"
                >
                  <Sunrise className="w-4 h-4" />
                  {saving ? 'Iniciando...' : 'Iniciar el Día'}
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Income from Sales */}
            <div className="lg:col-span-2 space-y-6">
              {/* Income from Vaccinations */}
              <Card data-testid="income-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-emerald-600">
                    <ArrowDownCircle className="w-5 h-5" />
                    Ingresos por Vacunaciones
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="p-4 rounded-lg bg-slate-50">
                      <div className="flex items-center gap-2 mb-2">
                        <CreditCard className="w-4 h-4 text-slate-400" />
                        <span className="text-xs font-medium text-slate-500 uppercase">Débito</span>
                      </div>
                      <p className="text-lg font-semibold font-mono text-slate-900">
                        {formatCurrency(cashData?.debito)}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-50">
                      <div className="flex items-center gap-2 mb-2">
                        <CreditCard className="w-4 h-4 text-slate-400" />
                        <span className="text-xs font-medium text-slate-500 uppercase">Crédito</span>
                      </div>
                      <p className="text-lg font-semibold font-mono text-slate-900">
                        {formatCurrency(cashData?.credito)}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-50">
                      <div className="flex items-center gap-2 mb-2">
                        <Banknote className="w-4 h-4 text-slate-400" />
                        <span className="text-xs font-medium text-slate-500 uppercase">Efectivo</span>
                      </div>
                      <p className="text-lg font-semibold font-mono text-slate-900">
                        {formatCurrency(cashData?.efectivo)}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-50">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-4 h-4 text-slate-400" />
                        <span className="text-xs font-medium text-slate-500 uppercase">Transfer.</span>
                      </div>
                      <p className="text-lg font-semibold font-mono text-slate-900">
                        {formatCurrency(cashData?.transferencia)}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-50">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-4 h-4 text-slate-400" />
                        <span className="text-xs font-medium text-slate-500 uppercase">Otro</span>
                      </div>
                      <p className="text-lg font-semibold font-mono text-slate-900">
                        {formatCurrency(cashData?.otro)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-600">Total Transbank:</span>
                        <span className="text-lg font-semibold font-mono">
                          {formatCurrency(totalTransbank)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-600">Total Ingresos:</span>
                        <span className="text-xl font-bold font-mono text-emerald-600">
                          {formatCurrency(totalIngresos)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Expenses */}
              <Card data-testid="expenses-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-600">
                    <ArrowUpCircle className="w-5 h-5" />
                    Gastos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="gastos_agua">Gastos Bidones Agua</Label>
                      <Input
                        id="gastos_agua"
                        type="number"
                        value={formData.gastos_agua}
                        onChange={(e) => setFormData({...formData, gastos_agua: parseFloat(e.target.value) || 0})}
                        className="input-clinical mt-1"
                        disabled={cerrada}
                        data-testid="input-gastos-agua"
                      />
                    </div>
                    <div>
                      <Label htmlFor="gastos_reembolso">Gastos por Reembolso</Label>
                      <Input
                        id="gastos_reembolso"
                        type="number"
                        value={formData.gastos_reembolso}
                        onChange={(e) => setFormData({...formData, gastos_reembolso: parseFloat(e.target.value) || 0})}
                        className="input-clinical mt-1"
                        disabled={cerrada}
                        data-testid="input-gastos-reembolso"
                      />
                    </div>
                    <div>
                      <Label htmlFor="otros_gastos">Otros Gastos</Label>
                      <Input
                        id="otros_gastos"
                        type="number"
                        value={formData.otros_gastos}
                        onChange={(e) => setFormData({...formData, otros_gastos: parseFloat(e.target.value) || 0})}
                        className="input-clinical mt-1"
                        disabled={cerrada}
                        data-testid="input-otros-gastos"
                      />
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-200 flex justify-end">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-600">Total Gastos:</span>
                      <span className="text-xl font-bold font-mono text-red-600">
                        {formatCurrency(totalGastos)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Cash Control */}
            <div className="space-y-6">
              {/* Cash Control */}
              <Card data-testid="cash-control-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wallet className="w-5 h-5" />
                    Control de Caja
                  </CardTitle>
                  {cashData?.apertura_at && (
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Día iniciado a las {formatHora(cashData.apertura_at)}
                      {cashData?.abierto_por ? ` por ${cashData.abierto_por}` : ''}
                    </p>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Encargado</Label>
                    <EncargadoSelect disabled={cerrada} />
                  </div>
                  <div>
                    <Label htmlFor="caja_inicio">Caja Inicio Efectivo</Label>
                    <Input
                      id="caja_inicio"
                      type="number"
                      value={formData.caja_inicio_efectivo}
                      onChange={(e) => setFormData({...formData, caja_inicio_efectivo: parseFloat(e.target.value) || 0})}
                      className="input-clinical mt-1"
                      disabled={cerrada}
                      data-testid="input-caja-inicio"
                    />
                  </div>
                  <div>
                    <Label htmlFor="deposito">Depósito Bancario</Label>
                    <Input
                      id="deposito"
                      type="number"
                      value={formData.deposito_bancario}
                      onChange={(e) => setFormData({...formData, deposito_bancario: parseFloat(e.target.value) || 0})}
                      className="input-clinical mt-1"
                      disabled={cerrada}
                      data-testid="input-deposito"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Summary */}
              <Card className="bg-slate-900 text-white" data-testid="summary-card">
                <CardHeader>
                  <CardTitle className="text-white">Resumen del Día</CardTitle>
                  {lastUpdated && (
                    <p className="text-xs text-slate-400" data-testid="last-updated">
                      Ingresos actualizados a las {lastUpdated.toLocaleTimeString('es-CL')} · se refrescan cada 30 s
                    </p>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Caja Inicio:</span>
                    <span className="font-mono">{formatCurrency(formData.caja_inicio_efectivo)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">+ Efectivo Ventas:</span>
                    <span className="font-mono text-emerald-400">{formatCurrency(cashData?.efectivo)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">- Total Gastos:</span>
                    <span className="font-mono text-red-400">{formatCurrency(totalGastos)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">- Depósito Bancario:</span>
                    <span className="font-mono text-amber-400">{formatCurrency(formData.deposito_bancario)}</span>
                  </div>
                  <div className="pt-3 mt-3 border-t border-slate-700">
                    <div className="flex justify-between">
                      <span className="font-medium">Total Caja Efectivo:</span>
                      <span className="text-xl font-bold font-mono text-blue-400">
                        {formatCurrency(totalCajaEfectivo)}
                      </span>
                    </div>
                  </div>
                  {cerrada && cashData?.cerrado_por && (
                    <p className="text-xs text-slate-400 pt-2">
                      Cerrada por {cashData.cerrado_por} a las {formatHora(cashData.cierre_at)}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Action Buttons */}
              {cerrada ? (
                <div className="space-y-3">
                  <Button 
                    onClick={downloadCierrePdf}
                    className="w-full gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="btn-descargar-cierre-pdf"
                  >
                    <Download className="w-4 h-4" />
                    Descargar Cierre PDF
                  </Button>
                  <Button 
                    onClick={handleReabrir}
                    variant="outline"
                    className="w-full gap-2"
                    disabled={saving}
                    data-testid="btn-reabrir-caja"
                  >
                    <Unlock className="w-4 h-4" />
                    {saving ? 'Reabriendo...' : 'Reabrir Caja'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Button 
                    onClick={handleSave}
                    className="w-full btn-accent gap-2"
                    disabled={saving}
                    data-testid="save-btn"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Guardando...' : 'Guardar Datos'}
                  </Button>
                  <Button 
                    onClick={handleCerrar}
                    className="w-full gap-2 bg-slate-800 hover:bg-slate-900 text-white"
                    disabled={saving}
                    data-testid="btn-cerrar-caja"
                  >
                    <Lock className="w-4 h-4" />
                    {saving ? 'Cerrando...' : 'Cerrar Caja del Día'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DailyCashPage;
